import React, { useEffect, useRef, useState } from 'react';
import { Wifi, Phone, Globe, Network, Server, Shield, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ServicesSection = () => {
  const sectionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();
  const [showScrollHint, setShowScrollHint] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Smooth scroll to the section
            entry.target.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start'
            });
            
            // Fade in animation
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        setShowScrollHint(scrollContainerRef.current.scrollLeft < 10);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const handleScrollClick = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft += 300;
    }
  };

  const headingLine = {
    width: '90px',
    height: '3px',
    backgroundColor: '#400684',
    margin: '10px auto 0',
    marginTop: '-20px',
    marginBottom: '20px',
    transition: 'width 0.3s ease',
  };

  const containerStyle = {
    padding: '40px 20px',
    maxWidth: '100%',
    margin: '0 auto',
    opacity: '0',
    transform: 'translateY(20px)',
    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  };

  const titleStyle = {
    fontSize: '52px',
    fontWeight: 'bold',
    marginBottom: '20px',
    textAlign: 'center',
    fontFamily: 'Oswald, sans-serif',
    color: '#400684',
  };
  const descriptionStyle = {
    fontSize: '16px',
    color: '#666',
    marginLeft: '20px',
    marginBottom: '10px',
    maxWidth: '800px',
    alignSelf: 'flex-start',
    textAlign: 'left',
    width: '100%',
    '@media screen and (max-width: 768px)': {
      alignSelf: 'center !important',
      textAlign: 'center !important',
      marginLeft: '0 !important',
      margin: '0 auto !important',
      maxWidth: '90% !important'
    }
};

  const headerContainerStyle = {
    display: 'flex',
    justifyContent: 'flex-start', // Desktop left-aligned
    alignItems: 'center',
    marginBottom: '20px',
    marginLeft: '20px', // Desktop left margin
    '@media (max-width: 768px)': {
      justifyContent: 'center',
      marginLeft: '0',
    }
  };

  const servicesContainerStyle = {
    position: 'relative',
    marginBottom: '30px',
  };

  const servicesScrollContainer = {
    display: 'flex',
    overflowX: 'auto',
    gap: '30px',
    padding: '20px 0',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    scrollBehavior: 'smooth',
  };

  const scrollHintStyle = {
    position: 'absolute',
    right: '20px',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(64, 6, 132, 0.9)',
    color: 'white',
    padding: '15px',
    borderRadius: '50%',
    display: showScrollHint ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'bounceRight 1.5s infinite',
    zIndex: 2,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
  };

  const buttonStyle = {
    display: 'inline-block',
    padding: '15px 30px',
    backgroundColor: '#400684',
    color: 'white',
    borderRadius: '8px',
    fontSize: '16px',
    fontFamily: 'Oswald, sans-serif',
    fontWeight: '600',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    border: 'none',
    cursor: 'pointer',
    marginTop:'20px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  };

  const serviceCardStyle = {
    flex: '0 0 300px',
    padding: '30px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.15)',
    cursor: 'pointer',
  };

  const iconStyle = {
    color: '#400684',
    marginBottom: '20px',
    width: '40px',
    height: '40px',
  };

  const serviceNameStyle = {
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '15px',
    color: '#333',
  };

  const serviceDescStyle = {
    fontSize: '15px',
    color: '#666',
    lineHeight: '1.6',
  };

 
  const scrollStyles = `
    .services-scroll::-webkit-scrollbar {
      display: none;
    }
    .services-scroll {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    @keyframes bounceRight {
      0%, 100% {
        transform: translateX(0) translateY(-50%);
      }
      50% {
        transform: translateX(10px) translateY(-50%);
      }
    }
    .service-card:hover {
      transform: translateY(-5px);
      box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    }
    .section-container {
      scroll-margin-top: 80px;
    }
   @media (max-width: 768px) {
      .heading-line {
        display: none;
      }
      .section-container {
        min-height: auto !important;
        padding: 20px 10px !important;
      }
      h2 {
        font-size: 28px !important;
        margin-bottom: 10px !important;
        text-align: center;
      }
      #p {
        font-size: 14px !important;
        margin: 0 auto !important;
        text-align: center;
        max-width: 100% !important;
        padding: 0 20px;
      }
      .header-container {
        justify-content: center !important;
      }
      .services-scroll {
        gap: 15px;
      }
      .service-card {
        flex: 0 0 250px !important;
        padding: 20px !important;
      }
      .service-card h3 {
        font-size: 18px !important;
      }
      .service-card p {
        font-size: 13px !important;
      }
    }
  `;

  return (
    <>
         <style>{scrollStyles}</style>
      <div style={containerStyle} ref={sectionRef} className="section-container">
        <h2 style={titleStyle}>DEALING IN ALL PROFESSIONAL IT SERVICES</h2>
        <div style={headingLine} className="heading-line"></div>
        <p id='p' style={descriptionStyle}>
          Whether you are looking to monitor and connect your business branches, 
          hoping to obtain high-speed DIA internet from your service provider, 
          are dreaming of an innovative, agile, cost-efficient solution using our unique IoT, 
          or are looking for excellent business voice services and bundles. 
          we are here to serve you with tailored solutions and packages.
        </p>

        <div 
          style={headerContainerStyle} 
          className="header-container"
        >
          <button 
            onClick={() => navigate('/services')}
            style={buttonStyle}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#2d0466';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 8px rgba(0, 0, 0, 0.15)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#400684';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
            }}
          >
            View All Services
          </button>
        </div>
        <div style={servicesContainerStyle}>
          {showScrollHint && (
            <div 
              style={scrollHintStyle}
              onClick={handleScrollClick}
            >
              <ChevronRight size={24} />
            </div>
          )}
          <div 
            style={servicesScrollContainer} 
            className="services-scroll"
            ref={scrollContainerRef}
          >
            <div style={serviceCardStyle} className="service-card">
              <Wifi style={iconStyle} />
              <h3 style={serviceNameStyle}>High-Speed Internet</h3>
              <p style={serviceDescStyle}>Dedicated Internet Access (DIA) with guaranteed high-speed connectivity for your business.</p>
            </div>

            <div style={serviceCardStyle} className="service-card">
              <Phone style={iconStyle} />
              <h3 style={serviceNameStyle}>Voice Services</h3>
              <p style={serviceDescStyle}>Advanced business voice solutions with flexible bundling options and features.</p>
            </div>

            <div style={serviceCardStyle} className="service-card">
              <Network style={iconStyle} />
              <h3 style={serviceNameStyle}>IoT Solutions</h3>
              <p style={serviceDescStyle}>Innovative IoT implementations for smart, efficient business operations.</p>
            </div>

            <div style={serviceCardStyle} className="service-card">
              <Globe style={iconStyle} />
              <h3 style={serviceNameStyle}>Network Connectivity</h3>
              <p style={serviceDescStyle}>Secure and reliable branch connectivity solutions for your business network.</p>
            </div>

            <div style={serviceCardStyle} className="service-card">
              <Server style={iconStyle} />
              <h3 style={serviceNameStyle}>Infrastructure</h3>
              <p style={serviceDescStyle}>Complete IT infrastructure management and maintenance services.</p>
            </div>

            <div style={serviceCardStyle} className="service-card">
              <Shield style={iconStyle} />
              <h3 style={serviceNameStyle}>Security Services</h3>
              <p style={serviceDescStyle}>Comprehensive network and data security solutions for your business.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServicesSection;